import React from 'react';
import PropTypes from 'prop-types';
import { ToastContext, Modal, Button, SelectHierarchy, Message } from '@weezevent/nacre';
import * as Sentry from '@sentry/browser';

import { useTranslation } from 'react-i18next';
import Style from './changeRatePostAction.module.css';
import RatesTreeModel from '../../../models/ratesTreeModel';
import useAppContext from '../../../context/useAppContext';
import LoadingCard from '../../../components/loadingCard';
import { pruneEmptyGroups } from '../add-attendee/components/OrderStep/Select/Selector';

export default function ChangeRatePostAction({ actionInstance = null, onClose = () => {}, sync = () => {} }) {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const toast = React.useContext(ToastContext);
    const [{ event, organization, feature_flags }] = useAppContext();
    let [rates, loadingRates] = RatesTreeModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk
        },
        [organization, event]
    );

    const paidTicket = parseInt(actionInstance.items[0].paid_price) > 0 && actionInstance.items[0].order.status === 'valid';

    const ratesOptions = React.useMemo(() => {
        if (!rates) {
            return [];
        }

        // Rate change from free to paid is possible, it's used to send payment links to participants.
        // Only the change from paid to paid with a different price is blocked.
        return pruneEmptyGroups(
            rates.toOptions({
                filter: rate => {
                    if (rate.id === actionInstance.items[0].rate.id) return false;
                    if (!paidTicket) return true;

                    const fees = rate.fees.reduce((accumulator, currentValue) => accumulator + parseFloat(currentValue.amount), 0);
                    const total_price = parseFloat(rate.price) + fees;

                    return total_price === parseFloat(actionInstance.items[0].paid_price);
                }
            })
        );
    }, [rates, actionInstance, paidTicket]);

    const [selectedRate, setSelectedRate] = React.useState(null);

    const data = React.useMemo(() => {
        let _data = {
            rate_id: selectedRate,
            mode_accred: feature_flags.includes('EO_EXTIA'),
            ids: actionInstance.items.map(item => item.id)
        };
        return _data;
    }, [selectedRate, actionInstance, feature_flags]);

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        actionInstance
            .runPostAction(data, actionInstance.items[0])
            .then(() => {
                toast.success(t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.toast.success'));
                setIsSubmitting(false);
                onClose();
                sync();
            })
            .catch(e => {
                console.error(e);
                Sentry.captureException(e);
                toast.error(t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.toast.error'));
                setIsSubmitting(false);
            });
    }, [actionInstance, onClose, data]);

    const _onClose = React.useCallback(() => {
        onClose();
        setIsSubmitting(false);
    }, [onClose]);

    if (loadingRates) {
        return null;
    }

    // Changing the rate of multiple tickets isn't supported (see is_available with the mode === 'detail' condition).
    // It would require changing the rate of all ticket in handleSubmit.
    // It should also make sure that they all have the same price.
    if (actionInstance.items.length > 1) {
        return null;
    }

    return (
        <Modal
            size="medium"
            open={Boolean(actionInstance)}
            onClose={_onClose}
            title={actionInstance ? t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.title') : ''}
            subtitle={
                actionInstance ? t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.subtitle', { ticket: actionInstance.items[0].id_weez_ticket }) : ''
            }
            submitButton={<Button primary onClick={handleSubmit} disabled={isSubmitting || !selectedRate} isLoading={isSubmitting} label={t('ticket.common.action.confirm')} />}
            cancelButton={<Button onClick={_onClose} label={t(`ticket.common.action.cancel`)} />}
        >
            <LoadingCard loading={loadingRates} height={'20vh'}>
                <SelectHierarchy
                    options={ratesOptions}
                    searchable
                    disabled={!ratesOptions.length}
                    placeholder={
                        ratesOptions.length
                            ? t('ticket.manager.attendees.action.change_rate.ratePlaceholder')
                            : t('ticket.manager.attendees.action.change_rate.paid_already.no_choice')
                    }
                    placeholderSearch={t('ticket.common.action.search')}
                    chevron
                    widthIndent={'15px'}
                    onChange={setSelectedRate}
                    value={selectedRate}
                />

                <div className={Style['change-rate']}>
                    {paidTicket && (
                        <Message type="default" title={t('ticket.manager.attendees.action.change_rate.paid_already.title')}>
                            {t('ticket.manager.attendees.action.change_rate.paid_already.text')}
                        </Message>
                    )}
                </div>
            </LoadingCard>
        </Modal>
    );
}

ChangeRatePostAction.propTypes = {
    actionInstance: PropTypes.object,
    onClose: PropTypes.func,
    sync: PropTypes.func
};
